import {
  type OwnerStatementSummariesDetailFilter as FilterType,
  useOwnerStatementSummariesDetailFilter,
} from './useOwnerStatementSummariesDetailFilter';
import { Group, SegmentedControl, Text, Tooltip } from '@mantine/core';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { CustomFilter } from '../../../../components';
import { Filter, type CalendarFilterDateType } from '@vrplatform/components';
import { day, hasValue } from '@vrplatform/utils';
import { useOwnerStatementSummariesDetailQuery } from './useOwnerStatementSummariesDetailQuery';
import { useDashboard, useJournalEntriesMinMaxQuery } from '@vrplatform/data';

type Props = {
  hideBookingChannel?: boolean;
};

export const OwnerStatementSummariesDetailFilter = (props: Props) => {
  const [dashboard] = useDashboard();

  return (
    <Group justify="space-between">
      <Group>
        <Month />
        {dashboard !== 'owner' && <Contact />}
        <Listings />
      </Group>
      <GroupBy {...props} />
    </Group>
  );
};

export const ownerStatementSummariesMonthRangeLabelFormatter = (
  v: CalendarFilterDateType | string | undefined | null
) => {
  if (!v || (Array.isArray(v) === true && v.filter(hasValue).length === 0))
    return 'Date Range';
  const value = Array.isArray(v) ? v : (v.split('...') as [string, string]);
  const [start, e] = value;
  const end = e || day(start).endOf('month').yyyymmdd();

  const isSameMonth =
    day(start).month() === day(end).month() &&
    day(start).year() === day(end).year();
  if (isSameMonth) return day(start).format('MMM YYYY');

  const isYearRange =
    day(start).yyyymmdd() === day(end).startOf('year').yyyymmdd() &&
    day(end).endOf('month').yyyymmdd() === day(end).endOf('year').yyyymmdd();

  if (isYearRange) return day(start).format('YYYY');
  return `${day(start).format('MMM YYYY')} - ${day(end).format('MMM YYYY')}`;
};

const Month = () => {
  const { filter, setFilter } = useOwnerStatementSummariesDetailFilter();
  return (
    <OwnerStatementSummariesMonthsFilter
      monthRange={filter.monthRange}
      setMonthRange={(v) => setFilter({ monthRange: v })}
    />
  );
};

type MonthRange = `${string}...${string}`;

export const OwnerStatementSummariesMonthsFilter = ({
  monthRange,
  setMonthRange,
}: {
  monthRange: MonthRange;
  setMonthRange: (updated: MonthRange) => void;
}) => {
  const queryData = useJournalEntriesMinMaxQuery({ range: 'month' });

  const minDate = queryData.data?.minDate
    ? day(queryData.data.minDate).startOf('year').yyyymmdd()
    : undefined;
  const maxDate = queryData.data?.maxDate
    ? day(queryData.data.maxDate).endOf('year').yyyymmdd()
    : undefined;

  const value = useMemo<CalendarFilterDateType>(() => {
    if (!monthRange) return [null, null];
    const [start, end] = monthRange.split('...');
    return [day(start).toDate(), day(end).startOf('month').toDate()];
  }, [monthRange]);

  return (
    <Filter.Month
      value={value}
      label="Month"
      type="range"
      minDate={minDate}
      maxDate={maxDate}
      labelFormatter={ownerStatementSummariesMonthRangeLabelFormatter}
      setValue={(dates) => {
        const start = dates?.[0] ? day(dates[0]).yyyymmdd() : null;
        const end = day(dates?.[1] || dates?.[0])
          .endOf('month')
          .yyyymmdd();

        if (!start || !end) return;

        setMonthRange(`${start}...${end}`);
      }}
      defaultDisplayedDate={value[0] ? day(value[0]).yyyymmdd() : undefined}
    />
  );
};

const Contact = () => {
  const { filter, setFilter } = useOwnerStatementSummariesDetailFilter();

  return (
    <CustomFilter.Contacts
      contactIds={filter.contactId ? [filter.contactId] : []}
      setContactIds={(ids) => {
        const filtered = ids?.filter((x) => x !== filter.contactId);
        setFilter({ contactId: filtered?.[0] });
      }}
      type="owner"
      clearable={false}
    />
  );
};

const Listings = () => {
  const { filter, setListingIds } = useOwnerStatementSummariesDetailFilter();
  const ownerIds = useMemo(
    () => (filter.contactId ? [filter.contactId] : undefined),
    [filter.contactId]
  );

  return (
    <CustomFilter.Listings
      listingIds={filter.listingIds}
      setListingIds={setListingIds}
      ownerIds={ownerIds}
    />
  );
};

const TYPE_OPTIONS: Record<FilterType['view'], string> = {
  listing: 'By Listing',
  month: 'By Month',
  bookingChannel: 'By Booking Channel',
  reservation: 'By Reservation',
};

const GroupBy = ({ hideBookingChannel }: Props) => {
  const queryData = useOwnerStatementSummariesDetailQuery();

  const { filter, setView } = useOwnerStatementSummariesDetailFilter();

  const layout = queryData.data?.layouts?.find(
    (layout) => layout.layoutId === queryData.layoutId
  );
  const aggregated = layout?.netRevenueSection?.aggregate;

  type View = FilterType['view'];

  const data = useMemo(() => {
    const opts = Object.entries(TYPE_OPTIONS).map(([v, label]) => {
      const value = v as View;
      if (aggregated && value === 'reservation') {
        return {
          label: (
            <Tooltip label="Not available for aggregated net revenue layouts">
              <Text inherit span>
                {label}
              </Text>
            </Tooltip>
          ) as any,
          value,
          disabled: true,
        };
      }

      return {
        label,
        value,
      };
    });

    if (hideBookingChannel) {
      return opts.filter((x) => x.value !== 'bookingChannel');
    }

    return opts;
  }, [aggregated, hideBookingChannel]);

  return (
    <GroupByControl
      key={`${filter.view}-${aggregated ? 'aggregate' : 'detail'}-${
        hideBookingChannel ? 'hide-booking-channel' : 'show-booking-channel'
      }`}
      data={data}
      view={filter.view}
      setView={setView}
    />
  );
};

const GroupByControl = ({
  data,
  view,
  setView,
}: {
  data: { label: ReactNode; value: FilterType['view']; disabled?: boolean }[];
  view: FilterType['view'];
  setView: (view: FilterType['view']) => void;
}) => {
  const [optimisticView, setOptimisticView] = useState(view);
  const viewFrameRef = useRef<number | null>(null);

  return (
    <SegmentedControl
      data={data}
      size="sm"
      value={optimisticView}
      onChange={(value) => {
        if (!value || value === optimisticView) return;

        const nextView = value as FilterType['view'];

        setOptimisticView(nextView);

        if (viewFrameRef.current !== null) {
          cancelAnimationFrame(viewFrameRef.current);
        }

        viewFrameRef.current = requestAnimationFrame(() => {
          setView(nextView);
          viewFrameRef.current = null;
        });
      }}
      styles={(theme) => ({
        innerLabel: {
          paddingInline: theme.spacing.xs,
        },
      })}
    />
  );
};
